import { BlcLoader, Body, Message } from '@Backlot-Cars/archie';
import { notifyError } from 'helpers/errorNotifier';
import OktaService from 'helpers/okta';
import { useEffect, useMemo, useRef, useState } from 'react';
import UserSessionService from 'services/navigation/userSession.service';
import { LoaderWrapper, OktaWrapper } from './styles';
import {
	WIDGET_CONTAINER_ID,
	OKTA_INVALID_CREDENTIALS_ERROR_CODE,
	getOktaErrorCode,
	getWidgetEmailValue,
	getOktaWidgetConfig,
} from './helpers';

import '@okta/okta-signin-widget/css/okta-sign-in.min.css';

const trackFailedOktaLogin = async (email) => {
	if (!email) return;
	try {
		// As login has handled on Frontend, we need to notify backend about failed login attempt
		await UserSessionService.instance.oktaLoginFailTracking({ email });
	} catch (error) {
		notifyError(error, { metadata: { email } });
	}
};

const OktaSignInWidget = ({ loginWithAccessToken, errorMessage, requestIsLoading, onInvalidCredentials }) => {
	const [isClient, setIsClient] = useState(false);
	const signInRef = useRef(null);
	const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);

	const authClient = useMemo(() => {
		return OktaService.getInstance().oktaAuth;
	}, []);

	useEffect(() => {
		// Ensure we're on the client side
		setIsClient(true);
	}, []);

	useEffect(() => {
		if (requestIsLoading) {
			// Hide or show the widget based on loading state, this hides is but does not unmount
			signInRef.current?.hide();
		} else {
			signInRef.current?.show();
		}
	}, [requestIsLoading]);

	const onSuccessfulLogin = async (tokens) => {
		const accessToken = tokens?.accessToken?.accessToken;
		if (accessToken) {
			authClient.tokenManager.setTokens(tokens);
			await loginWithAccessToken(accessToken);
		} else {
			notifyError(new Error('Okta widget returned no access token'));
		}
	};

	useEffect(() => {
		if (!isClient) return;
		// check if session exists and only load widget if no session
		let isMounted = true;

		// Dynamically import Okta widget only on client side
		const loadOktaWidget = async () => {
			try {
				// Dynamic import to avoid SSR issues
				const { default: OktaSignIn } = await import('@okta/okta-signin-widget');
				if (!isMounted) return;

				// Initialize Okta Sign-In Widget
				const signIn = new OktaSignIn(getOktaWidgetConfig(authClient));
				signInRef.current = signIn;

				signIn
					.showSignInToGetTokens({ el: `#${WIDGET_CONTAINER_ID}` })
					.then(onSuccessfulLogin)
					.catch((err) => {
						notifyError(err, { reason: 'Error during Okta widget auth' }, err);
					});
			} catch (error) {
				notifyError(error, { reason: 'Failed to load Okta widget' }, error);
			}

			signInRef.current?.on('afterError', function (context, error) {
				const errorCode = getOktaErrorCode(error);
				if (errorCode === OKTA_INVALID_CREDENTIALS_ERROR_CODE) {
					const email = getWidgetEmailValue();
					trackFailedOktaLogin(email);
					onInvalidCredentials?.();
					return;
				} else {
					notifyError(error, { reason: 'Okta widget error' }, error);
				}
			});

			signInRef.current?.on('afterRender', function () {
				setIsWidgetLoaded(true);
			});
		};

		loadOktaWidget();

		return () => {
			isMounted = false;
			if (signInRef.current) {
				signInRef.current.remove();
				signInRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isClient]);

	return (
		<>
			{(!isWidgetLoaded || requestIsLoading) && (
				<LoaderWrapper>
					<BlcLoader />
				</LoaderWrapper>
			)}
			{/* once okta success we need to make a request to our BE if that returns an error we show it here */}
			{Boolean(errorMessage) && (
				<Message color="alert" variant="inline">
					<Body>{errorMessage}</Body>
				</Message>
			)}
			<OktaWrapper id={WIDGET_CONTAINER_ID} />
		</>
	);
};

export default OktaSignInWidget;
